import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import SocialMediaPopup from "./SocialMediaPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const SocialMediaWidget = ({ setFieldValue, values = {}, errors = {} }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  // Define fields with estimated completion times
  const fields = [
    { name: "Facebook", key: "facebookPage", estimatedTime: "2 min", required: false },
    { name: "Instagram", key: "instagram", estimatedTime: "2 min", required: false },
    { name: "LinkedIn", key: "linkedinCompany", estimatedTime: "2 min", required: false },
    { name: "X (Twitter)", key: "twitter", estimatedTime: "2 min", required: false },
    { name: "TikTok", key: "tiktok", estimatedTime: "2 min", required: false },
    { name: "YouTube", key: "youtube", estimatedTime: "2 min", required: false },
    { name: "Pinterest", key: "pinterest", estimatedTime: "2 min", required: false }
  ];

  // Sample data for demonstration
  const sampleData = {
    facebookPage: "",
    instagram: "",
    linkedinCompany: "",
    twitter: "",
    tiktok: "",
    youtube: "",
    pinterest: ""
  };

  // Merge sample data with actual values
  const displayValues = {
    facebookPage: values.facebookPage || sampleData.facebookPage || "",
    instagram: values.instagram || sampleData.instagram || "",
    linkedinCompany: values.linkedinCompany || sampleData.linkedinCompany || "",
    twitter: values.twitter || sampleData.twitter || "",
    tiktok: values.tiktok || sampleData.tiktok || "",
    youtube: values.youtube || sampleData.youtube || "",
    pinterest: values.pinterest || sampleData.pinterest || ""
  };

  // Calculate completion percentage
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  // Get social media platforms that are configured
  const getConfiguredPlatforms = () => {
    const configured = [];
    if (displayValues.facebookPage) configured.push("Facebook");
    if (displayValues.instagram) configured.push("Instagram");
    if (displayValues.linkedinCompany) configured.push("LinkedIn");
    if (displayValues.twitter) configured.push("X (Twitter)");
    if (displayValues.tiktok) configured.push("TikTok");
    if (displayValues.youtube) configured.push("YouTube");
    if (displayValues.pinterest) configured.push("Pinterest");
    return configured;
  };

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Social Media</AdminPanelTitle>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
              <div 
                className="h-full bg-freshgreen transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
          </div>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
              const isCompleted = hasValue;
              
              return (
                <div key={field.key} className="flex items-center justify-between">
                    {progress === 100 ? (
                      <div 
                        className={`group flex items-center w-full py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <AdminTableBody emphasis className="w-56 text-offblack group-hover:text-primaryblue">
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className="text-offblack group-hover:text-primaryblue truncate">
                          {isCompleted ? displayValues[field.key] : `Estimated ${field.estimatedTime}`}
                        </AdminMainText>
                      </div>
                    ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? 'text-offblack group-hover:text-primaryblue' : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack group-hover:text-primaryblue truncate max-w-[200px]`}>
                          {isCompleted ? displayValues[field.key] : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? 'Completed' : 'Start'}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <SocialMediaPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          initialStep={selectedFieldIndex}
        />
      )}
    </>
  );
};

export default SocialMediaWidget;
